"use client";

import React, { memo } from "react";
import Link from "next/link";
import { CheckCircle, ArrowRight, Star } from "lucide-react";
import { AnimatedCard } from "@hooks/useSmoothScrollAnimations";
import AbisLogoIcon from "@components/ui/AbisLogoIcon";
import { trackButtonClick } from "@utils/ga4Tracking";
import { createMarkup } from "@app/agentic-bi-tool/utils/htmlSanitizer";
import { SafeHTMLRenderer } from "@utils/homeDataLoader";
import ValuePropositionBadge from "@components/ui/ValuePropositionBadge";

interface CTASectionProps {
  data: {
    seoChip: string;
    titleLine1: string;
    titleLine2: string;
    description: string;
    primaryButton: {
      text: string;
      action: string;
      href?: string;
    };
    secondaryButton?: {
      text: string;
      action: string;
    };
    tertiaryButton?: {
      text: string;
      action: string;
      href?: string;
    };
    benefits: string[];
  };
  onPrimaryClick: () => void;
  onSecondaryClick: () => void;
  variant?: "default" | "abis"; // "abis" for ABIS landing page with logo and tracking
  useSafeHTML?: boolean; // Use SafeHTMLRenderer instead of createMarkup
}

const CTASection = memo<CTASectionProps>(
  ({
    data,
    onPrimaryClick,
    onSecondaryClick,
    variant = "default",
    useSafeHTML = false,
  }) => {
    const isAbisVariant = variant === "abis";
    const secondaryButton = data.secondaryButton;
    const isPrimaryCalendlyLink =
      data.primaryButton.href?.includes("calendly.com");
  const calendlyRel = "nofollow noopener noreferrer";

    return (
      <section
        id="cta"
        className={`w-full py-20 relative overflow-visible ${
          isAbisVariant
            ? "bg-gradient-to-b from-transparent to-abis-primary"
            : "bg-gradient-to-br from-abis-primary to-abis-primary/80 backdrop-blur-sm"
        }`}
      >
        <div className="container mx-auto px-4 max-w-6xl relative z-10">
          {/* Header */}
          <AnimatedCard
            id="cta-header"
            animation="fadeInUp"
            delay={0}
            className="text-center mb-12 bg-transparent shadow-none p-0 hover:shadow-none"
            config={{ threshold: 0.1, rootMargin: "50px 0px" }}
          >
            <ValuePropositionBadge
              seoChip={data.seoChip}
              titleLine1={data.titleLine1}
              titleLine2={data.titleLine2}
              icon={Star}
              className="text-white"
            />
            {/* Description with HTML support */}
            {useSafeHTML ? (
              <div className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed mt-6">
                <SafeHTMLRenderer content={data.description} />
              </div>
            ) : (
              <p
                className={`${isAbisVariant ? "text-xl" : "text-lg"} text-white/90 max-w-3xl mx-auto leading-relaxed mt-6`}
                dangerouslySetInnerHTML={createMarkup(data.description)}
              />
            )}
          </AnimatedCard>

          {/* Benefits List */}
          <AnimatedCard
            id="cta-benefits"
            animation="fadeInUp"
            delay={0.2}
            className="mb-12 bg-transparent shadow-none p-0 hover:shadow-none"
            config={{ threshold: 0.1, rootMargin: "50px 0px" }}
          >
            <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {data.benefits.map((benefit, index) => (
                <li
                  key={index}
                  className="flex items-center space-x-3 bg-gray-50/10 rounded-2xl p-5"
                >
                  <CheckCircle className="w-6 h-6 text-abis-accent flex-shrink-0 transition-all duration-300" />
                  <span className="text-white/90 font-medium transition-colors duration-300">
                    {benefit}
                  </span>
                </li>
              ))}
            </ul>
          </AnimatedCard>

          {/* CTA Buttons */}
          <AnimatedCard
            id="cta-buttons"
            animation="fadeInUp"
            delay={0.4}
            className="text-center p-6 bg-transparent shadow-none hover:shadow-none"
            config={{ threshold: 0.1, rootMargin: "50px 0px" }}
          >
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              {data.primaryButton.href ? (
                <Link
                  href={data.primaryButton.href}
                  {...(isPrimaryCalendlyLink
                    ? { target: "_blank", rel: calendlyRel }
                    : {})}
                  className="btn btn-hero font-bold text-base py-3 px-4 md:px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-abis-accent/50 focus:ring-offset-2 focus:ring-offset-abis-primary"
                  aria-label={`${data.primaryButton.text} - ${data.primaryButton.href}`}
                  title={data.primaryButton.text}
                >
                  {isAbisVariant ? (
                    <span className="flex items-center justify-center">
                      <AbisLogoIcon
                        size="md"
                        className="mr-2"
                        aria-hidden="true"
                      />
                      {data.primaryButton.text}
                    </span>
                  ) : (
                    data.primaryButton.text
                  )}
                </Link>
              ) : (
                <button
                  onClick={onPrimaryClick}
                  className="group relative btn btn-hero font-bold text-base py-3 px-4 md:px-6 w-auto rounded-xl border border-transparent box-border transition-all duration-300 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-abis-accent/50 focus:ring-offset-2 focus:ring-offset-abis-primary overflow-hidden flex items-center justify-center"
                  aria-label={`${data.primaryButton.text} - Hauptaktion`}
                  title={data.primaryButton.text}
                  role="button"
                  tabIndex={0}
                >
                  {isAbisVariant ? (
                    <span className="flex items-center justify-center">
                      <AbisLogoIcon
                        size="md"
                        className="mr-2"
                        aria-hidden="true"
                      />
                      {data.primaryButton.text}
                    </span>
                  ) : (
                    data.primaryButton.text
                  )}
                </button>
              )}

              {secondaryButton && (
                <button
                  onClick={() => {
                    if (isAbisVariant) {
                      trackButtonClick(
                        "scroll_to_hero",
                        "final_cta_section",
                        "secondary_button",
                      );
                    }
                    onSecondaryClick();
                  }}
                  className="group relative bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-semibold text-base py-3 px-4 md:px-6 w-auto rounded-xl border border-white/20 hover:border-white/40 box-border transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-abis-primary overflow-hidden flex items-center justify-center"
                  aria-label={`${secondaryButton.text} - Sekundäre Aktion für weitere Informationen`}
                  title={secondaryButton.text}
                  role="button"
                  tabIndex={0}
                >
                  {secondaryButton.text}
                  <ArrowRight
                    className="inline-block w-5 h-5 ml-2 transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </button>
              )}

              {data.tertiaryButton &&
                (data.tertiaryButton.action === "open-video" ? (
                  <button
                    onClick={() => {
                      if (isAbisVariant) {
                        trackButtonClick(
                          "demo_video",
                          "final_cta_section",
                          "tertiary_button",
                        );
                      }
                      onSecondaryClick();
                    }}
                    className="btn btn-hero bg-white/10 backdrop-blur-sm text-white font-semibold text-base py-3 px-4 md:px-6 rounded-xl transition-all duration-200 border border-white/20 hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-abis-primary"
                    aria-label={`${data.tertiaryButton.text} - Demo-Video ansehen`}
                    title={data.tertiaryButton.text}
                    role="button"
                    tabIndex={0}
                  >
                    {data.tertiaryButton.text}
                    <ArrowRight
                      className="inline-block w-5 h-5 ml-2 transition-transform"
                      aria-hidden="true"
                    />
                  </button>
                ) : (
                  <Link
                    href={data.tertiaryButton.href || "#"}
                    {...(data.tertiaryButton.href?.startsWith("http")
                      ? {
                          target: "_blank",
                          rel: data.tertiaryButton.href.includes("calendly.com")
                            ? calendlyRel
                            : "noopener noreferrer",
                        }
                      : {})}
                    onClick={() => {
                      if (isAbisVariant) {
                        trackButtonClick(
                          data.tertiaryButton?.href?.startsWith("http")
                            ? "calendly_meeting"
                            : "success_stories",
                          "final_cta_section",
                          "tertiary_button",
                        );
                      }
                    }}
                    className="btn btn-hero bg-white/10 backdrop-blur-sm text-white font-semibold text-base py-3 px-4 md:px-6 rounded-xl transition-all duration-200 border border-white/20 hover:bg-white/20 focus:outline-none focus:ring-4 focus:ring-white/30 focus:ring-offset-2 focus:ring-offset-abis-primary"
                    aria-label={
                      data.tertiaryButton.href?.startsWith("http")
                        ? `${data.tertiaryButton.text} - Calendly Termin vereinbaren (öffnet in neuem Tab)`
                        : `${data.tertiaryButton.text} - Erfolgsgeschichten ansehen`
                    }
                    title={data.tertiaryButton.text}
                  >
                    {data.tertiaryButton.text}
                    <ArrowRight
                      className="inline-block w-5 h-5 ml-2 transition-transform"
                      aria-hidden="true"
                    />
                  </Link>
                ))}
            </div>
          </AnimatedCard>
        </div>
      </section>
    );
  },
);

CTASection.displayName = "CTASection";

export default CTASection;
